// Guided-tour scripts for every dashboard role AND guest / iPad flows.
// Each step: { title, body (HTML), tab (optional testid suffix for tab-<x>), target (data-testid), cta }
// If `tab` is present, the tour auto-selects that tab before displaying the step.
// If `target` is present, the tour highlights that element with a spotlight.

const CAL_STEPS = [
  {
    title: "The Calendar",
    body: "This weekly grid shows every scheduled treatment. <b>Accent-tinted tiles</b> are couples treatments (two guests in one appointment). Auto-refreshes every 20 seconds.",
    tab: "calendar",
    target: "tab-calendar",
  },
  {
    title: "Read a booking",
    body: "Click any tile to open the details — guest, treatment, duration, special request notes and the guest's answers to treatment questions. Everything you need before the session.",
    tab: "calendar",
  },
  {
    title: "Bookings list",
    body: "A chronological list with search & filters (by crew, status, upcoming/past). Best view for a daily briefing.",
    tab: "list",
    target: "tab-list",
  },
];

const OPS_STEPS = [
  {
    title: "Trips & guests",
    body: "Charters live here — arrival, departure, and the guest roster with cabin assignment, 4-digit PIN, and female checkbox (for female-only treatment questions).",
    tab: "trips",
    target: "tab-trips",
  },
  {
    title: "Cabins",
    body: "The rooms on the yacht. Set an <b>iPad PIN</b> to enable the shared-cabin iPad view; a live <i>iPad connected</i> badge shows when a device is signed in.",
    tab: "cabins",
    target: "tab-cabins",
  },
  {
    title: "Treatments",
    body: "Add each treatment with a photo, price, durations (multiple allowed), min-notice override, category, couples toggle, and question forms — including an optional female-only block.",
    tab: "treatments",
    target: "tab-treatments",
  },
  {
    title: "Categories",
    body: "Group treatments (Massage · Facial · Wellness) so the guest menu stays organised and elegant.",
    tab: "categories",
    target: "tab-categories",
  },
  {
    title: "Crew",
    body: "Your therapists — photo, active toggle, break windows, and which treatments each is trained for. The guest picker uses this list to offer availability.",
    tab: "crew",
    target: "tab-crew",
  },
  {
    title: "Settings",
    body: "Global rules — open hours, minimum notice (minutes), prep-time buffer, branding, and email transport (SMTP) with a <b>Send test</b> button.",
    tab: "settings",
    target: "tab-settings",
  },
];

const USERS_STEP_MGR = [
  {
    title: "Users — you can manage Crew & Supervisor",
    body: "Create <b>Supervisor</b> and <b>Crew</b> accounts here, with profile photos and role-scoped access. <i>Admin and Developer accounts are out of reach — by design.</i>",
    tab: "users",
    target: "tab-users",
  },
];

const USERS_STEP_ADMIN = [
  {
    title: "Users — you manage everyone below Admin",
    body: "Create and edit <b>Manager</b>, <b>Supervisor</b> and <b>Crew</b> accounts. Admin accounts can only be created or promoted by a Developer.",
    tab: "users",
    target: "tab-users",
  },
];

const INTRO = (roleLabel) => ({
  title: `Welcome, ${roleLabel}`,
  body: "This is a short hands-on tour of the <b>Carinthia VII</b> spa platform. Each step highlights a real button — click <b>Next</b> to walk through. You can close and re-open the tour anytime.",
});
const OUTRO = {
  title: "You're all set",
  body: "That's the tour. Re-launch <b>SOP training</b> from the sidebar whenever you need a refresher.<br /><br /><i>Fair winds ⚓</i>",
};

export function stepsForRole(role) {
  if (role === "crew") return [
    INTRO("Crew"),
    { title: "Your access", body: "As <b>Crew</b>, you have <b>view-only</b> access. You'll see two tabs on the left; everything else is hidden." },
    ...CAL_STEPS,
    { title: "You cannot edit", body: "Booking details are read-only for Crew. If a guest asks to reschedule or cancel, ping a Manager or Admin — they have the controls." },
    OUTRO,
  ];
  if (role === "supervisor") return [
    INTRO("Supervisor"),
    { title: "Your access", body: "You have full <b>operational</b> access. Every tab except <i>Users</i> and <i>Platform tools</i>. You can reschedule, cancel and delete any booking." },
    ...CAL_STEPS,
    ...OPS_STEPS,
    { title: "One limit", body: "You cannot manage user accounts. Ask a <b>Manager</b>, <b>Admin</b> or <b>Developer</b> when a new teammate needs access." },
    OUTRO,
  ];
  if (role === "manager") return [
    INTRO("Manager"),
    { title: "Your access", body: "Everything a Supervisor has, <b>plus</b> user management for Supervisor and Crew accounts." },
    ...CAL_STEPS,
    ...OPS_STEPS,
    ...USERS_STEP_MGR,
    OUTRO,
  ];
  if (role === "admin") return [
    INTRO("Admin"),
    { title: "Your access", body: "Full app access. You can manage <b>Manager, Supervisor and Crew</b> accounts and every operational tab. Only Developers can touch Admin accounts and Platform tools." },
    ...CAL_STEPS,
    ...OPS_STEPS,
    ...USERS_STEP_ADMIN,
    OUTRO,
  ];
  // Developer / fallback = broadest tour with a note.
  return [
    INTRO("Developer"),
    { title: "Developer power", body: "You see everything, including <b>Platform tools</b> — logs, DB stats, health checks and bug reports. Handle with care.", tab: "platform", target: "tab-platform" },
    ...CAL_STEPS,
    ...OPS_STEPS,
    ...USERS_STEP_ADMIN,
    OUTRO,
  ];
}

// -----------------------
// Guest (cabin-PIN flow)
// -----------------------
export const GUEST_STEPS = [
  {
    title: "Welcome aboard Carinthia VII",
    body: "This quick tour shows how to browse the spa menu, book a treatment, and manage your reservations — all from this device.",
  },
  {
    title: "Browse the menu",
    body: "Treatments are grouped by category — <b>massage, facial, wellness</b>. Tap any card to see full details, durations and price.",
    target: "menu-title",
  },
  {
    title: "Reserve a treatment",
    body: "Choose your duration, add a note if you have preferences, and pick a time in <b>5-minute steps</b>. Female-only questions appear automatically if applicable.",
  },
  {
    title: "Confirm and relax",
    body: "You'll see a confirmation with all your booking details and answers. We take it from there — a reminder arrives before your appointment.",
  },
  {
    title: "My reservations",
    body: "Every booking you've made is here. You can review or cancel upcoming ones any time — no need to call the spa.",
    target: "nav-reservations",
  },
  { title: "Enjoy your stay", body: "That's it — the spa is a tap away. <br /><br /><i>Fair winds ⚓</i>" },
];

// -----------------------
// iPad shared-cabin flow
// -----------------------
export const IPAD_STEPS = [
  {
    title: "Welcome aboard Carinthia VII",
    body: "This iPad is <b>shared with your cabin</b>. You can see all appointments for everyone in the cabin from one screen — no re-login needed.",
  },
  {
    title: "Your cabin — at a glance",
    body: "Each guest tile shows their upcoming count. Tap a guest to open their personal reservations.",
    target: "cabin-guests",
  },
  {
    title: "Common calendar",
    body: "The timeline below groups every cabin booking by day — <b>Today · Tomorrow · Sun 26 Jul</b> — so you always know what's next.",
    target: "cabin-calendar",
  },
  {
    title: "Book a treatment",
    body: "Tap <b>Book a treatment</b>, choose who it's for, then follow the guided booking flow. You can jump back to this screen any time with the <b>Cabin view</b> link at the top.",
    target: "cabin-book-new",
  },
  {
    title: "Sign out when you leave",
    body: "The iPad stays connected as long as you want it to. <b>Sign out</b> if you're passing the tablet to another cabin — otherwise it's ready when you return.",
    target: "cabin-signout",
  },
  { title: "Enjoy your stay", body: "That's the full tour. <br /><br /><i>Fair winds ⚓</i>" },
];

export function displayRoleName(role) {
  return { crew: "Crew", supervisor: "Supervisor", manager: "Manager", admin: "Admin", developer: "Developer" }[role] || role;
}
